import React, { useContext } from 'react';
import { useRouter } from 'next/router';
import Loader from 'components/Loader';
import Error from 'components/Error';
import { getDocByType, getDocByTypeAndUid } from 'helpers/PrismicKnowledgeHub';
import { globalPropsRequests } from 'utils/globalProps';
import { StorefrontContext } from 'providers/storefront';
import Copyright from 'containers/Layout/Footer/Copyright';
import KnowledgeHubHeader from 'components/NewKnowledgeHub/KnowledgeHubHeader';
import Script from 'next/script';
import KnowledgeHubBreadcrumbs from 'components/NewKnowledgeHub/KnowledgeHubBreadcrumbs';
import FeaturedList from 'components/NewKnowledgeHub/FeaturedList';
import Head from 'next/head';

function TopicPage({ content }) {
  const { setOpenSearch } = useContext(StorefrontContext);
  const router = useRouter();
  const isNotMainPage = router.asPath.split('/').length > 2;

  const { isFallback } = useRouter();

  if (isFallback) {
    return <Loader />;
  }

  if (!content) {
    return <Error code={404} />;
  }

  const discussions = content.discussions;
  const topic = content.topic;
  const posts = content.posts.sort((a, b) =>
    a.last_publication_date.localeCompare(b.last_publication_date),
  );
  const seoTitle = content?.topic?.data?.seo_title;
  const seoDescription = content?.topic?.data?.seo_description;
  const ogImage = content?.topic?.data?.og_image;
  const ogDescription = content?.topic?.data?.og_description;

  // console.log('discussions ==>> ', discussions);
  // console.log('posts ==>> ', posts);
  // console.log('topic ==>> ', topic);

  return (
    <>
      <Head>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:description" content={ogDescription} />
        <meta
          property="og:image"
          content={
            ogImage?.url
              ? ogImage?.url
              : 'https://cdn.shopify.com/s/files/1/0522/3320/7988/files/ogImage.png?v=1713964496'
          }
        />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1024" />
      </Head>
      <Script
        src="https://static.cdn.prismic.io/prismic.js?new=true&repo=aftershock-knowledge-hub"
        strategy="lazyOnload"
      />
      <div className="body-wrapper">
        <KnowledgeHubHeader isNotMainPage={isNotMainPage} />
        <div className="container" onClick={() => setOpenSearch(false)}>
          <div className="content">
            <KnowledgeHubBreadcrumbs
              data={content.topic.data}
              topic={content.topic}
            />
            <div className="title-wrapper">
              <h1>{content.topic.data.topic_title}</h1>
              <h3>{content.topic.data.topic_description}</h3>
            </div>
            <FeaturedList
              topicUid={topic.uid}
              discussions={discussions}
              posts={posts}
            />
          </div>
        </div>
        <div className="bottom-wrapper">
          <div className="question">
            <div className="question-text">
              <h2>Didn’t Find an Answer?</h2>
              <span>
                Our team is just an e-mail away and ready to answer your
                question!
              </span>
            </div>
            <div>
              <button className="button">CONTACT US</button>
            </div>
          </div>
          <Copyright />
        </div>
      </div>
      <style jsx>{`
        .body-wrapper {
          display: grid;
          grid-template-rows: auto 1fr auto;
          min-height: 100vh;
        }
        .container {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          background-color: #1a1a1a;
          font-family: 'Roboto-Normal';
        }
        .content {
          width: 100%;
          max-width: 1230px;
          padding: 20px;
        }
        .breadcrumbs-container {
          display: flex;
          justify-content: flex-start;
          align-items: center;
        }
        .title-wrapper {
          display: flex;
          flex-direction: column;
          align-items: start;
          padding: 10px 0 20px;
        }
        .title-wrapper > h1 {
          margin: 0;
          font-family: 'Eurostile-Normal';
          text-align: center;
          font-size: 60px;
          letter-spacing: 3px;
        }
        @media (max-width: 767px) {
          .title-wrapper > h1 {
            font-size: 36px;
            text-align: left;
          }
        }
        .title-wrapper > h3 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
        }
        @media (max-width: 767px) {
          .title-wrapper > h3 {
            display: none;
          }
        }
        .question {
          display: none;
        }
        @media (max-width: 800px) {
          .question {
            width: 100%;
            padding: 40px;
            display: flex;
            justify-content: space-evenly;
            align-items: center;
            background-color: #1a1a1a;
          }
        }
        .question-text {
          width: 68%;
          margin-right: 5px;
        }
        .question-text > h2 {
          font-family: 'Eurostile-Bold';
          font-size: 24px;
          margin: 0;
        }
        .question-text > span {
          font-family: 'Roboto-Normal';
          font-size: 12px;
        }
        .button {
          font-family: 'Eurostile-Normal';
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 1px;
          text-align: center;
          padding: 12px 24px;
          color: #e4e4e4;
          background: #950810;
          border: none;
          border-radius: 4px;
        }
      `}</style>
    </>
  );
}

export async function getStaticProps({ params, preview = false, previewData }) {
  const ref = previewData?.ref || null;
  const { topic } = params;
  const global = {};

  await Promise.all(globalPropsRequests)
    .then((responses) => {
      responses.forEach((res) => {
        if (res) {
          const [id, body] = res;
          global[id] = body.data;
        }
      });
    })
    .catch(() => null);

  const topicData = await getDocByTypeAndUid('topic', topic, { ref });

  if (!topicData) {
    return {
      notFound: true,
    };
  }

  const posts = await getDocByType('post', { ref });
  const discussions = await getDocByType('discussion', { ref });

  const results = {
    topic: {
      uid: topic,
      data: topicData.data,
    },
    posts: posts,
    discussions: discussions,
  };

  return {
    props: {
      content: results || null,
      global,
      preview,
    },
    revalidate: 3600,
  };
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: true,
  };
}

export default TopicPage;
